import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatTime12Hour } from "@/lib/utils";
import { getStoredTimezone, getTimezoneAbbr } from "@/lib/timezone-utils";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek } from "date-fns";

interface Session {
  id: string;
  scheduled_at: string;
  scheduled_start_time: string;
  duration_minutes: number;
  status: string;
  profiles: {
    first_name: string;
    last_name: string;
  };
  mentors: {
    first_name: string;
    last_name: string;
  };
}

export function AdminSessionCalendar() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { toast } = useToast();

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          id,
          scheduled_at,
          scheduled_start_time,
          duration_minutes,
          status,
          profiles!sessions_student_id_fkey (first_name, last_name),
          mentors!sessions_mentor_id_fkey (first_name, last_name)
        `)
        .in('status', ['scheduled', 'pending', 'completed'])
        .order('scheduled_at', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast({
        title: "Error",
        description: "Failed to load sessions.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getSessionsForDay = (day: Date) => {
    return sessions.filter(session => 
      isSameDay(new Date(session.scheduled_at), day)
    );
  };

  const previousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  return (
    <Card className="obsidian-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-energy flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Sessions Calendar
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={previousMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium min-w-[120px] text-center">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <Button variant="outline" size="sm" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading sessions...
          </div>
        ) : (
          <div className="space-y-4">
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {calendarDays.map((day, index) => {
                const daySessions = getSessionsForDay(day);
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isToday = isSameDay(day, new Date());
                
                return (
                  <div
                    key={index}
                    className={`
                      min-h-[100px] border rounded-lg p-2
                      ${!isCurrentMonth ? 'bg-muted/20 text-muted-foreground' : ''}
                      ${isToday ? 'border-primary border-2' : 'border-border'}
                    `}
                  >
                    <div className="text-xs font-medium mb-1">
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-1">
                      {daySessions.map(session => (
                        <div
                          key={session.id}
                          className={`text-[10px] rounded px-1 py-0.5 truncate ${
                            session.status === 'completed' 
                              ? 'bg-green-500/10 border border-green-500/20' 
                              : 'bg-primary/10 border border-primary/20'
                          }`}
                          title={`${new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))} ${getTimezoneAbbr(getStoredTimezone())} - ${session.mentors.first_name} ${session.mentors.last_name} with ${session.profiles.first_name} ${session.profiles.last_name} (${session.status})`}
                        >
                          <div className="font-medium">
                            {new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))}
                          </div>
                          <div className="truncate">
                            {session.mentors.first_name} → {session.profiles.first_name}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* All Sessions List */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3 text-sm">All Sessions</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {sessions.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    No sessions found
                  </div>
                ) : (
                  sessions.slice(0, 10).map(session => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between border rounded-lg p-2 text-sm hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="font-medium">
                          {session.mentors.first_name} {session.mentors.last_name} → {session.profiles.first_name} {session.profiles.last_name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(session.scheduled_at), 'MMM d, yyyy')} at {new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: getStoredTimezone() }).format(new Date(session.scheduled_at))} {getTimezoneAbbr(getStoredTimezone())} • {session.duration_minutes}min
                        </div>
                      </div>
                      <Badge 
                        variant={
                          session.status === 'completed' ? 'default' : 
                          session.status === 'pending' ? 'secondary' : 
                          'outline'
                        } 
                        className={`text-xs ${session.status === 'completed' ? 'bg-green-500/10 text-green-600 border-green-500/20' : ''}`}
                      >
                        {session.status}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
